import { memo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MapPin, Calendar, Heart } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import OptimizedImage from "@/components/ui/optimized-image";
import { cn } from "@/lib/utils";
import { getTripCardImage, getTripCardSrcSet } from "@/lib/imageUtils";
import { countryNameToSlug } from "@/data/countryContent";
import { useLanguage } from "@/hooks/useLanguage";
import { useDefaultCoverImages } from "@/hooks/useDefaultCoverImages";
import { useTripPrefetch } from "@/hooks/useTripData";

interface TripCardProps {
  tripId: string;
  title: string;
  country: string;
  startDate?: string;
  endDate?: string;
  image: string;
  tripType?: "trip" | "vacation";
  showLikeButton?: boolean;
  isLiked?: boolean;
  onLikeToggle?: () => void;
  onClick?: () => void;
  priority?: boolean;
}

const TripCard = memo(({ 
  tripId,
  title, 
  country, 
  startDate, 
  endDate, 
  image, 
  tripType,
  showLikeButton = false,
  isLiked = false,
  onLikeToggle,
  onClick,
  priority = false
}: TripCardProps) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { getLocalizedPath } = useLanguage();
  const { prefetch } = useTripPrefetch();
  const { getDefaultCoverImage } = useDefaultCoverImages();
  
  // Use default image based on country if no cover image is set
  // Pass tripId as seed to get varied images per trip
  const displayImage = image ? getTripCardImage(image) : getDefaultCoverImage(country, tripId);
  const srcSet = image ? getTripCardSrcSet(image) : '';
  
  // Generate blur placeholder for Unsplash images
  const blurPlaceholder = displayImage.includes('unsplash.com') 
    ? `${displayImage.split('?')[0]}?w=16&q=10&blur=20`
    : null;
  
  // Get locale for date formatting
  const locale = i18n.language === 'fr' ? 'fr-FR' : i18n.language === 'es' ? 'es-ES' : 'en-US';

  // Calculate duration and format it
  const formatDuration = (start: string, end: string): string => {
    const startDateObj = new Date(start);
    const endDateObj = new Date(end);
    const diffTime = Math.abs(endDateObj.getTime() - startDateObj.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 14) {
      // Less than 2 weeks: show days
      return i18n.language === 'fr' 
        ? `${diffDays} jour${diffDays > 1 ? 's' : ''}`
        : i18n.language === 'es'
        ? `${diffDays} día${diffDays > 1 ? 's' : ''}`
        : `${diffDays} day${diffDays > 1 ? 's' : ''}`;
    } else if (diffDays < 56) {
      // Less than 8 weeks: show weeks
      const weeks = Math.round(diffDays / 7);
      return i18n.language === 'fr'
        ? `${weeks} semaine${weeks > 1 ? 's' : ''}`
        : i18n.language === 'es'
        ? `${weeks} semana${weeks > 1 ? 's' : ''}`
        : `${weeks} week${weeks > 1 ? 's' : ''}`;
    } else {
      // 8 weeks or more: show months
      const months = Math.round(diffDays / 30);
      return i18n.language === 'fr'
        ? `${months} mois`
        : i18n.language === 'es'
        ? `${months} mes${months > 1 ? 'es' : ''}`
        : `${months} month${months > 1 ? 's' : ''}`;
    }
  };

  // Parse countries and create clickable badges
  const countries = country.split(", ").map(c => c.trim()).filter(Boolean);

  const handleCountryClick = (e: React.MouseEvent, countryName: string) => {
    e.stopPropagation();
    const slug = countryNameToSlug(countryName);
    navigate(getLocalizedPath(`/destination/${slug}`));
  };

  // Prefetch trip data on mouse enter for faster navigation
  const handleMouseEnter = useCallback(() => {
    prefetch(tripId);
  }, [tripId, prefetch]);
  
  return (
    <Card 
      className={cn(
        "group overflow-hidden hover:shadow-lg transition-all duration-300 border-border hover:border-primary/50",
        onClick && "cursor-pointer"
      )}
      onClick={onClick}
      onMouseEnter={handleMouseEnter}
    >
      <div className="relative h-48 overflow-hidden">
        <OptimizedImage
          src={displayImage}
          srcSet={srcSet || undefined}
          sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
          alt={title}
          priority={priority}
          blurDataURL={blurPlaceholder || undefined}
          className="group-hover:scale-110"
          containerClassName="h-full"
          width={450}
          height={192}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
        {showLikeButton && (
          <button
            aria-label={isLiked ? t("tripCard.unlike") : t("tripCard.like")}
            className={cn(
              "absolute top-3 right-3 p-2 rounded-full transition-all duration-300",
              "hover:scale-125 active:scale-90",
              isLiked 
                ? "text-red-500 drop-shadow-[0_0_6px_rgba(239,68,68,0.5)]" 
                : "text-white/80 hover:text-white drop-shadow-[0_1px_3px_rgba(0,0,0,0.5)]"
            )}
            onClick={(e) => {
              e.stopPropagation();
              onLikeToggle?.();
            }}
          >
            <Heart className={cn(
              "h-5 w-5 transition-transform duration-300",
              isLiked && "fill-current animate-[heartbeat_0.4s_ease-in-out]"
            )} />
          </button>
        )}
        {tripType && (
          <Badge className="absolute top-3 left-3 bg-primary text-primary-foreground">
            {tripType === "vacation" ? t("tripCard.vacation") : t("tripCard.trip")}
          </Badge>
        )}
      </div>
      
      <CardContent className="p-4">
        <h3 className="font-bold text-lg mb-2 line-clamp-1">{title}</h3>
        
        <div className="flex items-center gap-2 text-muted-foreground text-sm mb-3 flex-wrap">
          <MapPin className="h-4 w-4 text-primary flex-shrink-0" />
          {countries.map((c, index) => (
            <button
              key={c}
              onClick={(e) => handleCountryClick(e, c)}
              className="hover:text-primary hover:underline transition-colors"
            >
              {c}{index < countries.length - 1 ? "," : ""}
            </button>
          ))}
        </div>
        
        {startDate && (
          <div className="flex items-center gap-2 text-muted-foreground text-sm">
            <Calendar className="h-4 w-4 text-accent" />
            <span>
              {new Date(startDate).toLocaleDateString(locale, { month: "short", year: "numeric" })}
              {endDate && ` · ${formatDuration(startDate, endDate)}`}
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
});

TripCard.displayName = "TripCard";

export default TripCard;
